import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { api } from "@/lib/api";
import { ScriptureReference } from "@/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Edit, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ScriptureDisplay } from "@/components/scripture/ScriptureDisplay";
import { AppLayout } from "@/components/layout/AppLayout";

export default function ScriptureReferencePage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [reference, setReference] = useState<ScriptureReference | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchReference();
    }
  }, [id]);

  const fetchReference = async () => {
    try {
      setIsLoading(true);
      const response = await api(`/scripture-references/${id}`);
      setReference(response as ScriptureReference);
    } catch (error) {
      console.error("Error fetching scripture reference:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!reference || !confirm("Are you sure you want to delete this scripture reference?")) return;

    try {
      await api(`/scripture-references/${id}`, {
        method: "DELETE",
      });
      navigate("/scripture-references");
    } catch (error) {
      console.error("Error deleting scripture reference:", error);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="container mx-auto py-6 px-6">
          <div className="text-center">Loading scripture reference...</div>
        </div>
      </AppLayout>
    );
  }

  if (!reference) {
    return (
      <AppLayout>
        <div className="container mx-auto py-6 px-6">
          <div className="text-center">Scripture reference not found</div>
        </div>
      </AppLayout>
    );
  }

  const formatReference = () => {
    const baseRef = `${reference.book} ${reference.chapter}`;
    if (reference.verse_start === reference.verse_end) {
      return `${baseRef}:${reference.verse_start} - ${reference.text || ""}`;
    }
    return `${baseRef}:${reference.verse_start}-${reference.verse_end} - ${reference.text || ""}`;
  };

  return (
    <AppLayout>
    <div className="container mx-auto py-6 px-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/scripture-references")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Scripture Reference</h1>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <ScriptureDisplay
            reference={formatReference()}
            scriptureReferenceId={reference.id}
          />
          <div className="text-sm text-muted-foreground">
            Created {formatDistanceToNow(new Date(reference.created_at), { addSuffix: true })}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </Card>
    </div>
    </AppLayout>
  );
} 